import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef } from "react";

import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { Section, CTABand, FAQ, SectionHeader } from "@/components/site/Blocks";
import { CalcCard, CalcField, CalcRow, CalcStat, usd, pct } from "@/components/site/CalcKit";
import { ToolLeadGate } from "@/components/site/ToolLeadGate";
import { LiveDscrRateSection } from "@/components/rates/LiveDscrRateSection";
import { buildHead } from "@/lib/seo-head";
import { useShareableCalcState } from "@/lib/calc-share";
import { ShareResultsBar } from "@/components/site/ShareResultsBar";
import { CalculatorAiPanel } from "@/components/site/CalculatorAiPanel";
import { CalculatorStateGate } from "@/components/site/CalculatorStateGate";
import { trackCalcEstimateViewed, trackCalcInput } from "@/lib/calculator-analytics";
import { PageEssentials, PageOutro } from "@/components/site/PageEssentials";
import { CALCULATOR_ESTIMATE_DISCLOSURE } from "@/lib/ai-disclosure";
import { analyzeDeal } from "@/lib/investor-calcs";
import rentalDealAnalyzerHero from "@/assets/hero/hero-rental-deal-analyzer.jpg";

const FAQS = [
  {
    q: "What does the rental property deal analyzer calculate?",
    a: "It takes one set of inputs — purchase price, rehab, loan sizing, rate and term, rent, vacancy, operating expenses and the closing costs you enter — and returns estimated PITIA and debt service, DSCR, NOI, cap rate, monthly and annual cash flow, cash invested, cash-on-cash return, LTV, and the rent required to reach the DSCR you select. If you add refinance or exit assumptions, it also estimates refinance proceeds, equity and the cash left in the deal. Every figure is an illustrative estimate, not an offer of credit.",
  },
  {
    q: "Where do the rate, fees and closing costs come from?",
    a: "From you. This tool does not source or publish any rate, fee, closing cost, lender threshold or program eligibility rule. Any rate you type is treated as an assumption unless you copied it from our live pricing tool, and closing costs are whatever dollar figure you enter. Actual pricing and costs are established only after a complete application and full underwriting.",
  },
  {
    q: "How is DSCR calculated here?",
    a: "DSCR is gross monthly rent divided by estimated PITIA — principal and interest, plus monthly taxes, insurance and any association dues. That is the same formula used by our standalone DSCR calculator, because both read from the same shared math module. Individual lenders and programs may compute or adjust the ratio differently.",
  },
  {
    q: "How is NOI different from cash flow in this analyzer?",
    a: "NOI is effective gross income (rent and other income, less your vacancy assumption) minus operating expenses — taxes, insurance, association dues, management, maintenance and utilities or other operating costs. It excludes debt service. Cash flow subtracts the loan payment from NOI, so it reflects what is left after financing.",
  },
  {
    q: "Does this tell me whether I qualify or how much I am approved for?",
    a: "No. Nothing here is an eligibility determination, pre-qualification, approval, rate lock, or commitment to lend. The outputs describe the scenario you entered under the assumptions you entered. Eligibility, pricing and terms are subject to a complete application, credit and property review, appraisal where required, and full underwriting.",
  },
  {
    q: "Should I use interest-only or amortizing in the analyzer?",
    a: "Model whichever structure you are actually considering, and try both. Interest-only lowers the monthly payment, which raises modeled DSCR and year-one cash flow while leaving the balance unchanged. Amortizing pays the balance down, which matters if your exit assumes a refinance or sale. Availability of any structure varies by lender and program.",
  },
  {
    q: "What should I enter for vacancy, management and maintenance?",
    a: "Use figures grounded in your own submarket data and management agreement rather than a generic default. Because these expenses reduce NOI directly, changing them by a couple of percentage points can move cap rate and cash-on-cash materially. There is no preset here that claims to be typical.",
  },
  {
    q: "How does the refinance and exit section work?",
    a: "Enter the value you expect at refinance, the loan-to-value and rate/term you want to test, the refinance closing costs you expect, and how long you plan to hold. The analyzer estimates the payoff balance at that point, the new loan amount, gross and net proceeds, equity remaining, the new payment and DSCR, and the cash left in the deal with the resulting cash-on-cash return.",
  },
  {
    q: "Can I share or save a scenario?",
    a: "Yes. The share bar produces a link that encodes only the scenario numbers — no name, email, phone or address is ever included — plus a copy-and-paste summary of the key figures for an email or deal memo.",
  },
  {
    q: "Is this tool for owner-occupied homes?",
    a: "No. It is built for business-purpose, non-owner-occupied investment property financing. Simply Approved Mortgages is a licensed mortgage broker (NMLS #2620881) and arranges business-purpose investor financing across its approved footprint, with residential consumer lending licensed in Florida and Colorado.",
  },
];

export const Route = createFileRoute("/rental-property-deal-analyzer")({
  head: () =>
    buildHead({
      title: "Rental Property Deal Analyzer — DSCR, NOI & Cash Flow",
      description:
        "Free rental property deal analyzer: model purchase, rehab, financing, rent and expenses to estimate PITIA, DSCR, NOI, cap rate, cash flow, cash-on-cash and refinance proceeds.",
      url: "/rental-property-deal-analyzer",
      keywords:
        "rental property deal analyzer, investment property deal analyzer, DSCR deal analyzer, rental property calculator, real estate deal analysis calculator, NOI and cap rate calculator, cash on cash and DSCR calculator, rental property cash flow analysis, investment property underwriting calculator, refinance proceeds calculator",
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "Rental Property Deal Analyzer", url: "/rental-property-deal-analyzer" },
      ],
      service: { name: "Rental Property Deal Analyzer" },
      faqs: FAQS,
      extraJsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "Rental Property Deal Analyzer",
          applicationCategory: "FinanceApplication",
          operatingSystem: "Web",
          url: "/rental-property-deal-analyzer",
          description:
            "Free acquisition and deal-return analyzer for rental property investors: PITIA, DSCR, NOI, cap rate, cash flow, cash-on-cash return and refinance proceeds from user-entered assumptions.",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
        },
      ],
    }),
  component: DealAnalyzerPage,
});

type Inputs = {
  purchasePrice: number;
  rehab: number;
  ltvPct: number;
  ratePct: number;
  termYears: number;
  interestOnly: boolean;
  closingCosts: number;
  monthlyRent: number;
  otherIncomeMonthly: number;
  vacancyPct: number;
  taxesAnnual: number;
  insuranceAnnual: number;
  hoaMonthly: number;
  managementPct: number;
  maintenancePct: number;
  utilitiesOtherMonthly: number;
  targetDscr: number;
  useRefi: boolean;
  refiValue: number;
  refiLtvPct: number;
  refiRatePct: number;
  refiTermYears: number;
  refiClosingCosts: number;
  holdYears: number;
};

/**
 * Blank/zero starting point. No rate, fee, closing cost, LTV cap or DSCR floor
 * is pre-filled from lender or program data — the investor supplies every
 * assumption, and the two structural fields below are neutral math settings.
 */
const DEFAULT: Inputs = {
  purchasePrice: 0,
  rehab: 0,
  ltvPct: 0,
  ratePct: 0,
  termYears: 30,
  interestOnly: false,
  closingCosts: 0,
  monthlyRent: 0,
  otherIncomeMonthly: 0,
  vacancyPct: 0,
  taxesAnnual: 0,
  insuranceAnnual: 0,
  hoaMonthly: 0,
  managementPct: 0,
  maintenancePct: 0,
  utilitiesOtherMonthly: 0,
  targetDscr: 1.0,
  useRefi: false,
  refiValue: 0,
  refiLtvPct: 0,
  refiRatePct: 0,
  refiTermYears: 30,
  refiClosingCosts: 0,
  holdYears: 5,
};

function DealAnalyzerPage() {
  const { state: i, patch, shareUrl } = useShareableCalcState<Inputs>("deal_analyzer", DEFAULT);
  const set = <K extends keyof Inputs>(k: K, v: Inputs[K]) => {
    patch(k, v);
    trackCalcInput("deal_analyzer", String(k), v as never);
  };

  const m = useMemo(
    () =>
      analyzeDeal({
        purchasePrice: i.purchasePrice,
        rehab: i.rehab,
        ltvPct: i.ltvPct,
        ratePct: i.ratePct,
        termYears: i.termYears,
        interestOnly: i.interestOnly,
        closingCosts: i.closingCosts,
        monthlyRent: i.monthlyRent,
        otherIncomeMonthly: i.otherIncomeMonthly,
        vacancyPct: i.vacancyPct,
        taxesAnnual: i.taxesAnnual,
        insuranceAnnual: i.insuranceAnnual,
        hoaMonthly: i.hoaMonthly,
        managementPct: i.managementPct,
        maintenancePct: i.maintenancePct,
        utilitiesOtherMonthly: i.utilitiesOtherMonthly,
        targetDscr: i.targetDscr,
        refi: i.useRefi
          ? {
              futureValue: i.refiValue,
              ltvPct: i.refiLtvPct,
              ratePct: i.refiRatePct,
              termYears: i.refiTermYears,
              closingCosts: i.refiClosingCosts,
              holdYears: i.holdYears,
            }
          : undefined,
      }),
    [i],
  );

  const viewedRef = useRef(false);
  useEffect(() => {
    if (viewedRef.current) return;
    viewedRef.current = true;
    trackCalcEstimateViewed("deal_analyzer", {
      dscr: m.dscr ?? 0,
      cash_on_cash: m.cashOnCash ?? 0,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const aiFacts = [
    { label: "Loan amount (assumption)", value: usd(m.loanAmount) },
    { label: "LTV", value: pct(m.ltv, 1) },
    { label: "Estimated PITIA", value: `${usd(m.pitia)} / mo` },
    { label: "DSCR", value: m.dscr === null ? "—" : m.dscr.toFixed(2) },
    { label: "NOI (annual)", value: usd(m.noiAnnual) },
    { label: "Cap rate", value: pct(m.capRate) },
    { label: "Monthly cash flow", value: usd(m.monthlyCashFlow) },
    { label: "Cash invested", value: usd(m.cashInvested) },
    { label: "Cash-on-cash return", value: m.cashOnCash === null ? "—" : pct(m.cashOnCash, 1) },
    { label: `Rent needed for DSCR ${i.targetDscr.toFixed(2)}`, value: `${usd(m.rentNeededForTargetDscr)} / mo` },
    { label: "Breakeven rent", value: `${usd(m.breakevenRentMonthly)} / mo` },
    ...(m.refi
      ? [
          { label: "Estimated refinance loan", value: usd(m.refi.newLoanAmount) },
          { label: "Estimated net refinance proceeds", value: usd(m.refi.netProceeds) },
          { label: "Cash left in the deal", value: usd(m.refi.cashLeftInDeal) },
        ]
      : []),
  ];

  const dscrColor =
    m.dscr === null ? "text-foreground" : m.dscr >= 1.25 ? "text-emerald-600" : m.dscr >= 1 ? "text-amber-600" : "text-destructive";
  const cfColor = m.monthlyCashFlow >= 0 ? "text-emerald-600" : "text-destructive";

  return (
    <SiteShell>
      <PageHero
        eyebrow="Deal analyzer"
        title="Rental Property Deal Analyzer"
        subtitle="One place to underwrite the whole deal: purchase, rehab, financing, rent, vacancy, operating expenses and your own closing costs — with estimated PITIA, DSCR, NOI, cap rate, cash flow, cash-on-cash return and optional refinance proceeds. Every input is your assumption and every output is an illustrative estimate, not an offer or commitment to lend."
        primaryHref="/apply?product=dscr-loans"
        primaryLabel="Get Your Personalized Rate Quote"
        secondaryHref="/rates"
        secondaryLabel="See Today's Rates"
        image={{
          src: rentalDealAnalyzerHero,
          alt: "Investor reviewing a rental property deal analysis on a laptop with property photos, calculator and house keys on the desk",
        }}
      />
      <PageEssentials />

      <Section>
        <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[1.4fr_1fr]">
          <div className="space-y-6">
            <CalcCard title="1. Acquisition">
              <div className="grid gap-4 md:grid-cols-2">
                <CalcField label="Purchase price" value={i.purchasePrice} onChange={(v) => set("purchasePrice", v)} prefix="$" />
                <CalcField label="Rehab / improvements" value={i.rehab} onChange={(v) => set("rehab", v)} prefix="$" />
                <CalcField
                  label="Closing costs (your estimate)"
                  value={i.closingCosts}
                  onChange={(v) => set("closingCosts", v)}
                  prefix="$"
                  hint="Enter the dollar amount you expect. No fee or cost figure is pre-filled here."
                />
              </div>
            </CalcCard>

            <CalcCard title="2. Financing assumptions">
              <div className="grid gap-4 md:grid-cols-2">
                <CalcField
                  label="Loan-to-value"
                  value={i.ltvPct}
                  onChange={(v) => set("ltvPct", v)}
                  suffix="%"
                  step={1}
                  hint="Your assumed leverage. Not a program cap."
                />
                <CalcField
                  label="Interest rate (assumption)"
                  value={i.ratePct}
                  onChange={(v) => set("ratePct", v)}
                  suffix="%"
                  step={0.125}
                  hint="Treated as an assumption unless copied from our live pricing tool."
                />
                <CalcField label="Term (years)" value={i.termYears} onChange={(v) => set("termYears", v)} step={1} />
                <CalcField
                  label="Target DSCR to test"
                  value={i.targetDscr}
                  onChange={(v) => set("targetDscr", v)}
                  step={0.05}
                  hint="A ratio you want to test rent against, not a lender requirement."
                />
              </div>
              <label className="mt-4 flex items-center gap-2 text-sm text-foreground">
                <input
                  type="checkbox"
                  checked={i.interestOnly}
                  onChange={(e) => set("interestOnly", e.target.checked)}
                  className="h-4 w-4 rounded border-input"
                />
                Model an interest-only payment instead of amortizing
              </label>
              <p className="mt-2 text-xs text-muted-foreground">
                Estimated loan amount under these assumptions: <strong>{usd(m.loanAmount)}</strong> ({pct(m.ltv, 1)} of price).
                Availability of any rate, term or payment structure varies by lender, program and scenario.
              </p>
            </CalcCard>

            <CalcCard title="3. Income">
              <div className="grid gap-4 md:grid-cols-2">
                <CalcField label="Monthly rent" value={i.monthlyRent} onChange={(v) => set("monthlyRent", v)} prefix="$" />
                <CalcField label="Other monthly income" value={i.otherIncomeMonthly} onChange={(v) => set("otherIncomeMonthly", v)} prefix="$" hint="Pet rent, parking, storage, laundry." />
                <CalcField label="Vacancy assumption" value={i.vacancyPct} onChange={(v) => set("vacancyPct", v)} suffix="%" step={1} />
              </div>
            </CalcCard>

            <CalcCard title="4. Operating expenses">
              <div className="grid gap-4 md:grid-cols-2">
                <CalcField label="Property taxes (annual)" value={i.taxesAnnual} onChange={(v) => set("taxesAnnual", v)} prefix="$" />
                <CalcField label="Insurance (annual)" value={i.insuranceAnnual} onChange={(v) => set("insuranceAnnual", v)} prefix="$" />
                <CalcField label="HOA / association dues (monthly)" value={i.hoaMonthly} onChange={(v) => set("hoaMonthly", v)} prefix="$" />
                <CalcField label="Property management" value={i.managementPct} onChange={(v) => set("managementPct", v)} suffix="%" step={1} hint="Percent of effective gross income." />
                <CalcField label="Maintenance / repairs" value={i.maintenancePct} onChange={(v) => set("maintenancePct", v)} suffix="%" step={1} hint="Percent of gross scheduled income." />
                <CalcField label="Utilities / other (monthly)" value={i.utilitiesOtherMonthly} onChange={(v) => set("utilitiesOtherMonthly", v)} prefix="$" />
              </div>
            </CalcCard>

            <CalcCard title="5. Optional refinance / exit assumptions">
              <label className="flex items-center gap-2 text-sm text-foreground">
                <input
                  type="checkbox"
                  checked={i.useRefi}
                  onChange={(e) => set("useRefi", e.target.checked)}
                  className="h-4 w-4 rounded border-input"
                />
                Model a refinance at the end of the hold period
              </label>
              {i.useRefi ? (
                <div className="mt-4 grid gap-4 md:grid-cols-2">
                  <CalcField label="Value at refinance (your estimate)" value={i.refiValue} onChange={(v) => set("refiValue", v)} prefix="$" />
                  <CalcField label="Refinance loan-to-value" value={i.refiLtvPct} onChange={(v) => set("refiLtvPct", v)} suffix="%" step={1} />
                  <CalcField label="Refinance rate (assumption)" value={i.refiRatePct} onChange={(v) => set("refiRatePct", v)} suffix="%" step={0.125} />
                  <CalcField label="Refinance term (years)" value={i.refiTermYears} onChange={(v) => set("refiTermYears", v)} step={1} />
                  <CalcField label="Refinance closing costs (your estimate)" value={i.refiClosingCosts} onChange={(v) => set("refiClosingCosts", v)} prefix="$" />
                  <CalcField label="Hold period before refinance (years)" value={i.holdYears} onChange={(v) => set("holdYears", v)} step={1} />
                </div>
              ) : null}
            </CalcCard>
          </div>

          <aside className="space-y-4 lg:sticky lg:top-24 lg:self-start">
            <CalculatorStateGate calculator="deal_analyzer">
              <ToolLeadGate
                calculator="deal_analyzer"
                storageKey="deal-analyzer:lead-unlocked"
                product="Rental Property Deal Analyzer"
                sourcePage="Rental Property Deal Analyzer"
                title="Unlock your full deal analysis"
                subtitle="See DSCR, NOI, cap rate, cash-on-cash, rent needed and refinance proceeds together — and we'll email you a copy."
                getContext={() => ({
                  purchase_price: i.purchasePrice,
                  ltv_pct: i.ltvPct,
                  monthly_rent: i.monthlyRent,
                  dscr: m.dscr ?? 0,
                  cash_invested: Math.round(m.cashInvested),
                  monthly_cash_flow: Math.round(m.monthlyCashFlow),
                })}
              >
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <CalcStat label="DSCR (estimate)" value={m.dscr === null ? "—" : m.dscr.toFixed(2)} colorClass={dscrColor} />
                    <CalcStat label="Monthly cash flow" value={usd(m.monthlyCashFlow)} colorClass={cfColor} />
                    <CalcStat label="Cash-on-cash (yr 1)" value={m.cashOnCash === null ? "—" : pct(m.cashOnCash, 1)} />
                    <CalcStat label="Cap rate" value={pct(m.capRate)} />
                  </div>

                  <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
                    <h3 className="font-display text-base font-semibold text-foreground">Debt service & operations</h3>
                    <dl className="mt-3 space-y-2 text-sm">
                      <CalcRow label="Estimated loan amount" value={usd(m.loanAmount)} />
                      <CalcRow label="Down payment" value={usd(m.downPayment)} />
                      <CalcRow label="LTV" value={pct(m.ltv, 1)} />
                      <CalcRow label="Principal & interest" value={`${usd(m.pi)} / mo`} />
                      <CalcRow label="Estimated PITIA" value={`${usd(m.pitia)} / mo`} strong />
                      <CalcRow label="Annual debt service" value={usd(m.annualDebtService)} />
                      <CalcRow label="Effective gross income" value={`${usd(m.effectiveGrossIncomeMonthly)} / mo`} />
                      <CalcRow label="Operating expenses" value={`${usd(m.operatingExpensesMonthly)} / mo`} />
                      <CalcRow label="NOI" value={`${usd(m.noiAnnual)} / yr`} strong />
                      <CalcRow label="Cash invested" value={usd(m.cashInvested)} />
                      <CalcRow label={`Rent needed for DSCR ${i.targetDscr.toFixed(2)}`} value={`${usd(m.rentNeededForTargetDscr)} / mo`} />
                      <CalcRow label="Rent gap vs. entered rent" value={`${usd(m.rentGapMonthly)} / mo`} />
                      <CalcRow label="Breakeven rent" value={`${usd(m.breakevenRentMonthly)} / mo`} />
                    </dl>
                  </div>

                  {m.refi ? (
                    <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
                      <h3 className="font-display text-base font-semibold text-foreground">Refinance / exit scenario</h3>
                      <dl className="mt-3 space-y-2 text-sm">
                        <CalcRow label="Estimated payoff balance" value={usd(m.refi.payoffBalance)} />
                        <CalcRow label="New loan under these assumptions" value={usd(m.refi.newLoanAmount)} />
                        <CalcRow label="Gross proceeds" value={usd(m.refi.grossProceeds)} />
                        <CalcRow label="Refinance closing costs" value={usd(m.refi.closingCosts)} />
                        <CalcRow label="Net proceeds" value={usd(m.refi.netProceeds)} strong />
                        <CalcRow label="Equity after refinance" value={usd(m.refi.equityAfterRefi)} />
                        <CalcRow label="New PITIA" value={`${usd(m.refi.newPitia)} / mo`} />
                        <CalcRow label="DSCR after refinance" value={m.refi.newDscr === null ? "—" : m.refi.newDscr.toFixed(2)} />
                        <CalcRow label="Cash flow after refinance" value={`${usd(m.refi.newMonthlyCashFlow)} / mo`} />
                        <CalcRow label="Cash left in the deal" value={usd(m.refi.cashLeftInDeal)} strong />
                        <CalcRow
                          label="Cash-on-cash after refinance"
                          value={m.refi.cashOnCashAfterRefi === null ? "—" : pct(m.refi.cashOnCashAfterRefi, 1)}
                        />
                      </dl>
                    </div>
                  ) : null}

                  <Link
                    to="/apply"
                    search={{ product: "dscr-loans" }}
                    className="block rounded-md border border-brand-gold bg-brand-gold px-4 py-3 text-center text-sm font-semibold text-primary hover:bg-brand-gold-hover"
                  >
                    Get Your Personalized Rate Quote
                  </Link>
                </div>
              </ToolLeadGate>
              <ShareResultsBar
                calculator="deal_analyzer"
                shareUrl={shareUrl}
                subject="My rental property deal analysis"
                summaryTitle="Rental property deal analysis — key numbers"
                keyNumbers={aiFacts.slice(0, 6)}
              />
              <CalculatorAiPanel
                calculator="deal_analyzer"
                facts={aiFacts}
                suggestions={[
                  "Which input is hurting my DSCR the most?",
                  "How do NOI, cap rate and cash-on-cash differ in these results?",
                  "What happens to cash flow if my vacancy assumption is higher?",
                  "How does the refinance scenario change the cash left in the deal?",
                ]}
              />
              <p className="text-xs leading-relaxed text-muted-foreground">{CALCULATOR_ESTIMATE_DISCLOSURE}</p>
            </CalculatorStateGate>
          </aside>
        </div>
      </Section>

      <Section className="border-t border-border bg-muted/30">
        <div className="mx-auto max-w-4xl space-y-4 text-sm text-foreground/90">
          <SectionHeader
            title="Methodology and assumptions"
            intro="Every formula here comes from the same shared math module used by our standalone calculators, so the DSCR, PITIA, LTV and refinance figures match tool to tool."
          />
          <ul className="list-disc space-y-2 pl-5">
            <li>Loan amount = purchase price × the loan-to-value you enter. Down payment is the remainder of the price.</li>
            <li>
              Estimated PITIA = principal and interest (amortizing, or interest-only if you select it) + monthly taxes +
              monthly insurance + association dues.
            </li>
            <li>DSCR = gross monthly rent ÷ estimated PITIA.</li>
            <li>
              Effective gross income = (rent + other income) − your vacancy assumption. NOI = effective gross income −
              operating expenses (taxes, insurance, dues, management, maintenance, utilities/other). NOI excludes debt service.
            </li>
            <li>Cap rate = annual NOI ÷ (purchase price + rehab). Cash flow = NOI − principal and interest.</li>
            <li>Cash invested = down payment + rehab + the closing costs you enter. Cash-on-cash = annual cash flow ÷ cash invested.</li>
            <li>
              Refinance scenario: payoff balance is amortized to the end of your hold period (unchanged if interest-only), the
              new loan is your entered value × your entered loan-to-value, and net proceeds subtract the refinance closing costs
              you enter.
            </li>
            <li>
              No rate, fee, closing cost, loan-to-value cap, DSCR floor, or eligibility rule is supplied by this tool. Fields
              start blank or at zero, and any rate you type is an assumption unless it came from our live pricing tool.
            </li>
          </ul>
          <p className="text-xs text-muted-foreground">
            Outputs are illustrative estimates for business-purpose, non-owner-occupied investment property financing. They are
            not an eligibility determination, pre-qualification, approval, rate lock, or commitment to lend. Full assumptions and
            limitations for every tool on this site are documented in our{" "}
            <Link to="/legal/calculator-methodology" className="text-brand underline">
              calculator methodology
            </Link>
            .
          </p>
        </div>
      </Section>

      <LiveDscrRateSection
        strategyCards
        id="live-rates"
        eyebrow="Live pricing · DSCR"
        heading="Replace your rate assumption with live pricing"
        paragraphs={[
          "Pricing responses returned for a sample business-purpose DSCR scenario on non-owner-occupied investment property, including rate, points or lender credit, and estimated principal and interest from the same quote. Pricing changes intraday and is not a loan approval, commitment, or guarantee of financing.",
        ]}
        links={[
          { to: "/investment-property-rates", label: "Price your own DSCR scenario" },
          { to: "/dscr-loan-requirements", label: "DSCR loan requirements" },
        ]}
      />

      <Section>
        <div className="mx-auto max-w-4xl">
          <SectionHeader title="Deal analyzer FAQ" intro="What the analyzer does, what it does not do, and how to read it." />
          <div className="mt-6">
            <FAQ items={FAQS} />
          </div>
        </div>
      </Section>

      <Section className="border-t border-border bg-muted/30">
        <div className="mx-auto max-w-4xl">
          <h2 className="font-display text-2xl font-bold text-foreground">Related investor tools</h2>
          <ul className="mt-4 grid gap-3 sm:grid-cols-2">
            {[
              { to: "/dscr-calculator" as const, label: "DSCR calculator — rent against the payment" },
              { to: "/cash-on-cash-return-calculator" as const, label: "Cash-on-cash return calculator" },
              { to: "/cap-rate-calculator" as const, label: "Cap rate calculator" },
              { to: "/brrrr-calculator" as const, label: "BRRRR calculator — the full refinance loop" },
              { to: "/cash-out-refinance-calculator" as const, label: "Cash-out refinance calculator" },
              { to: "/closing-cost-calculator" as const, label: "Closing cost calculator" },
            ].map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="text-brand underline">
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </Section>

      <CTABand
        title="Have a licensed loan officer review the scenario"
        body="Share your deal assumptions and we'll walk through an illustrative business-purpose structure for it. No obligation, and nothing shown is an offer or commitment to lend."
      />
      <PageOutro />
    </SiteShell>
  );
}
